import { useEffect, useMemo } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';
import { useCreateEvent } from '../hooks/useCreateEvent';
import { useStreamingText } from './useStreamingText';
import type { Citation } from '../services/types';
import Markdown from 'markdown-to-jsx';
import { AnalyticsEvent } from '../constants/analyticsEvents';
import { useMediaQuery } from '@mantine/hooks';
import { cn } from '../utils/styles';
import {
  SystemMessageState,
  useChatContext,
} from '../context/use-chat-context';
import {
  createMarkdownOverrides,
  preprocessCitationMarkers,
} from '../utils/markdownOverrides';
import { navigateTo } from '../utils/url';
import { usePlaygroundCtaInterceptor } from '../hooks/usePlaygroundCtaInterceptor';
import {
  PERSISTENT_MESSAGE_TIMEOUT,
  DEFAULT_MESSAGE_TIMEOUT,
} from '../constants/common';
import { IconArrowRight, IconChevronDown } from '@tabler/icons-react';
import { useSuperbarTheme } from '../hooks/useSuperbarTheme';
import { TtsMuteButton } from '../TtsMuteButton';

const invokeCtaCallback = ({
  cta,
  storeId,
}: {
  cta: string;
  storeId?: string;
}) => {
  const windowObj = window as unknown as Record<string, unknown>;
  if (windowObj.onCTACallbacks) {
    (
      windowObj.onCTACallbacks as (data: {
        storeId?: string;
        cta: string;
      }) => void
    )({ cta, storeId });
  } else {
    console.warn('No CTA callback registered');
  }
};

interface Props {
  message: string;
  isStreamActive?: boolean;
  autoHideTimeout?: number;
  citations?: Citation[];
  cta?: {
    name: string;
    description: string;
    key: string;
    defaultAction?: {
      type: string;
      actionProps: {
        href?: string;
      };
    };
  };
  nonDefinedCTA?: { label: string; url: string };
  storeId?: string;
  onClose?: () => void;
  onRetry?: () => void;
  hasCode?: boolean;
  isTtsMuted?: boolean;
  toggleTtsMute?: () => void;
}

export const AssistantMessage = ({
  message,
  isStreamActive = false,
  autoHideTimeout,
  citations,
  cta,
  nonDefinedCTA,
  storeId,
  onRetry,
  onClose,
  hasCode,
  isTtsMuted,
  toggleTtsMute,
}: Props) => {
  const {
    superBarViewState,
    systemMessageState,
    setSystemMessageState,
    displayMode,
  } = useChatContext();
  const theme = useSuperbarTheme();
  const isDesktop = useMediaQuery('(min-width: 1280px)');
  const isMobile = useMediaQuery('(max-width: 767px)');

  const { trackEvent } = useAnalytics();
  const { createEvent } = useCreateEvent();
  const { interceptCtaClick } = usePlaygroundCtaInterceptor();

  const { displayText, isStreaming, hasDisplayableText } = useStreamingText({
    accumulatedText: message,
    isStreamActive,
  });

  const handleClick = () => {
    if (onRetry) onRetry();
    if (!effectiveCta) return;

    createEvent('cta_clicked', {
      ctaKey: effectiveCta.key,
      ctaName: effectiveCta.name,
    });

    const href = effectiveCta.defaultAction?.actionProps?.href;
    if (href) {
      if (interceptCtaClick(href)) return;
      navigateTo(href);
      return;
    }

    invokeCtaCallback({ cta: effectiveCta.key, storeId });
  };

  useEffect(() => {
    trackEvent(AnalyticsEvent.ANSWER_RESPONSE_SHOWN, { message });
  }, [message, trackEvent]);

  const isExpanded = true;

  useEffect(() => {
    if (
      !hasCode &&
      autoHideTimeout !== PERSISTENT_MESSAGE_TIMEOUT &&
      !isDesktop &&
      !isExpanded &&
      systemMessageState !== SystemMessageState.AI_FILTERS &&
      systemMessageState !== SystemMessageState.HIDDEN
    ) {
      const timeout = autoHideTimeout ?? DEFAULT_MESSAGE_TIMEOUT;
      const timer = setTimeout(() => {
        setSystemMessageState(SystemMessageState.HIDDEN);
      }, timeout);

      return () => clearTimeout(timer);
    }
  }, [
    systemMessageState,
    setSystemMessageState,
    isDesktop,
    hasCode,
    autoHideTimeout,
  ]);

  const markdownOverrides = useMemo(
    () => createMarkdownOverrides(citations),
    [citations]
  );

  const hasCloseButton = onClose && isExpanded;

  // When only nonDefinedCTA is present (no predefined CTA), promote it to the primary button.
  // When both exist, render the predefined CTA as primary and nonDefinedCTA as secondary.
  const effectiveCta =
    !cta && nonDefinedCTA
      ? {
          name: nonDefinedCTA.label,
          description: nonDefinedCTA.label,
          key: nonDefinedCTA.label,
          defaultAction: {
            type: 'link',
            actionProps: { href: nonDefinedCTA.url },
          },
        }
      : cta;
  const hasSecondaryCta = !!(cta && nonDefinedCTA);
  const hasCta = effectiveCta || onRetry;

  return (
    <div
      className={cn(
        'caddietw-h-full caddietw-w-full caddietw-relative caddietw-overflow-visible',
        'caddietw-flex caddietw-gap-4',
        hasSecondaryCta
          ? 'caddietw-flex-col caddietw-items-start'
          : 'caddietw-flex-row caddietw-items-center caddietw-justify-between',
        !hasSecondaryCta &&
          isMobile &&
          'caddietw-flex-col caddietw-items-start',
        theme.components.assistantMessage.className
      )}
    >
      {hasCloseButton && (
        <div
          onClick={onClose}
          className={cn(
            'caddie-close-btn caddietw-absolute caddietw-top-[-16px] caddietw-right-[-22px] caddietw-z-10 caddietw-cursor-pointer caddietw-flex caddietw-w-6 caddietw-h-6 caddietw-justify-center caddietw-items-center caddietw-shrink-0 caddietw-outline-none focus:caddietw-outline-none focus-visible:caddietw-outline-none',
            'caddietw-border caddietw-border-solid',
            'caddietw-transition-all caddietw-duration-200 caddietw-group',
            theme.components.assistantMessage.closeButton.className
          )}
          style={
            {
              '--caddie-close-btn-bg':
                theme.components.assistantMessage.closeButton.background,
              '--caddie-close-btn-bg-hover':
                theme.components.assistantMessage.closeButton.backgroundHover,
              '--caddie-close-btn-icon-color':
                theme.components.assistantMessage.closeButton.icon,
              '--caddie-close-btn-icon-hover':
                theme.components.assistantMessage.closeButton.iconHover,
              '--caddie-close-btn-border-color':
                theme.components.assistantMessage.closeButton.borderColor,
              '--caddie-close-btn-radius': theme.radii.round,
              '--caddie-close-btn-shadow':
                theme.shadows.systemMessageCloseButton,
            } as React.CSSProperties
          }
        >
          <IconChevronDown
            size={'16px'}
            strokeWidth={2.5}
            className="caddie-close-btn-icon"
          />
        </div>
      )}
      <div className="caddietw-min-w-0 caddietw-flex caddietw-flex-col">
        <div
          className={cn(
            '[&_p]:caddietw-my-0 [&_p]:caddietw-leading-6',
            isMobile && '[&_p]:caddietw-my-4 [&_p]:caddietw-leading-7'
          )}
        >
          {displayText ? (
            <Markdown
              id={'caddie-system-message-text'}
              className={cn(
                'caddietw-font-inter caddietw-text-[16px] caddietw-font-normal caddietw-leading-[24px]'
              )}
              style={{ color: theme.components.assistantMessage.text }}
              options={{ overrides: markdownOverrides }}
            >
              {preprocessCitationMarkers(displayText)}
            </Markdown>
          ) : null}
        </div>

        {toggleTtsMute && (
          <TtsMuteButton
            isMuted={isTtsMuted ?? false}
            onClick={toggleTtsMute}
            color={theme.colors.ink.tertiary}
          />
        )}
      </div>

      {hasCta && (
        <div
          className={cn(
            'caddietw-flex caddietw-flex-row caddietw-gap-2 caddietw-self-start md:caddietw-items-center caddie-cta-enter',
            !hasSecondaryCta && 'caddietw-shrink-0',
            !hasSecondaryCta && isMobile && 'caddietw-w-full caddietw-mt-2'
          )}
        >
          <button
            id={'caddie-system-message-cta-button'}
            onClick={handleClick}
            className={cn(
              'caddie-cta-btn caddietw-rounded-[25px] caddietw-text-[14px] caddietw-py-2 caddietw-px-6 caddietw-flex caddietw-font-semibold caddietw-leading-[18px] caddietw-border-0 caddietw-justify-center caddietw-items-center caddietw-whitespace-nowrap caddietw-cursor-pointer caddietw-gap-2 caddietw-transition-colors',
              theme.components.assistantMessage.ctaButton.className
            )}
            style={
              {
                '--caddie-cta-btn-bg':
                  theme.components.assistantMessage.ctaButton.background,
                '--caddie-cta-btn-bg-hover':
                  theme.components.assistantMessage.ctaButton.hoverBackground,
                '--caddie-cta-btn-color':
                  theme.components.assistantMessage.ctaButton.text,
                '--caddie-cta-btn-radius': theme.radii.pillButton,
              } as React.CSSProperties
            }
          >
            {effectiveCta?.name || 'Retry'}
            {isMobile && !hasSecondaryCta && <IconArrowRight size={18} />}
          </button>

          {hasSecondaryCta && nonDefinedCTA && (
            <a
              href={nonDefinedCTA.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (interceptCtaClick(nonDefinedCTA.url)) {
                  e.preventDefault();
                }
              }}
              className="caddie-cta-btn caddietw-rounded-[25px] caddietw-text-[14px] caddietw-py-2 caddietw-px-6 caddietw-flex caddietw-font-semibold caddietw-leading-[18px] caddietw-justify-center caddietw-items-center caddietw-whitespace-nowrap caddietw-cursor-pointer caddietw-no-underline caddietw-transition-colors"
              style={{
                border: `1px solid ${theme.colors.ink.secondary}`,
                color: theme.colors.ink.secondary,
                background: 'transparent',
                borderRadius: theme.radii.pillButton,
              }}
            >
              {nonDefinedCTA.label}
            </a>
          )}
        </div>
      )}
    </div>
  );
};
